import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

const TemplateSelectionModal = ({ isOpen, onClose, onSelectTemplate }) => {
    const templatesFromState = useSelector(state => state.settings.settings.projectTemplates);
    const templates = useMemo(() => templatesFromState || [], [templatesFromState]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [isCreating, setIsCreating] = useState(false);

    // Reset selection when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedTemplateId(null);
            setIsCreating(false);
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const handleConfirm = async () => {
        setIsCreating(true);
        try {
            const template = templates.find(t => t.id === selectedTemplateId);
            await onSelectTemplate(template || null);
            onClose();
        } catch (error) {
            console.error('Failed to create project:', error);
            setIsCreating(false);
        }
    };

    const handleCreateBlank = async () => {
        setIsCreating(true);
        try {
            await onSelectTemplate(null);
            onClose();
        } catch (error) {
            console.error('Failed to create project:', error);
            setIsCreating(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" data-modal="true">
            <div className="bg-[var(--surface-bg)] rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col shadow-xl" data-modal="true">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-[var(--surface-border)]">
                    <h2 className="text-xl font-semibold text-[var(--text-primary)]">
                        Create New Project
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-[var(--button-hover-bg)] rounded-md text-[var(--text-primary)] transition-colors"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    <p className="text-sm text-[var(--text-muted)]">
                        Choose a template to start with preset todos, or create a blank project.
                    </p>

                    {/* Blank Project Option */}
                    <div
                        onClick={() => setSelectedTemplateId(null)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedTemplateId === null
                                ? 'border-blue-500 bg-blue-500/10'
                                : 'border-[var(--input-border)] hover:border-[var(--input-border-hover)]'
                        }`}
                    >
                        <div className="flex items-start gap-3">
                            <span className="material-symbols-outlined text-[32px] text-[var(--text-primary)]">
                                note_add
                            </span>
                            <div className="flex-1">
                                <h3 className="font-medium text-[var(--text-primary)] mb-1">
                                    Blank Project
                                </h3>
                                <p className="text-sm text-[var(--text-muted)]">
                                    Start with an empty project and add your own tasks
                                </p>
                            </div>
                            {selectedTemplateId === null && (
                                <span className="material-symbols-outlined text-blue-500">
                                    check_circle
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Templates */}
                    {templates.length > 0 && (
                        <>
                            <div className="border-t border-[var(--surface-border)] pt-4">
                                <h3 className="font-medium text-[var(--text-primary)] mb-3">
                                    Templates ({templates.length})
                                </h3>
                                <div className="space-y-3">
                                    {templates.map(template => (
                                        <div
                                            key={template.id}
                                            onClick={() => setSelectedTemplateId(template.id)}
                                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                                selectedTemplateId === template.id
                                                    ? 'border-blue-500 bg-blue-500/10'
                                                    : 'border-[var(--input-border)] hover:border-[var(--input-border-hover)]'
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <span className="material-symbols-outlined text-[32px] text-[var(--text-primary)]">
                                                    {template.icon}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-medium text-[var(--text-primary)] mb-1">
                                                        {template.name}
                                                    </h3>
                                                    {template.todos.length > 0 && (
                                                        <div className="text-sm text-[var(--text-muted)]">
                                                            <p className="mb-1">
                                                                {template.todos.length} preset todo{template.todos.length !== 1 ? 's' : ''}:
                                                            </p>
                                                            <ul className="list-disc list-inside space-y-0.5 ml-2">
                                                                {template.todos.slice(0, 3).map((todo, idx) => (
                                                                    <li key={idx} className="truncate">{todo}</li>
                                                                ))}
                                                                {template.todos.length > 3 && (
                                                                    <li className="italic">
                                                                        ...and {template.todos.length - 3} more
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                                {selectedTemplateId === template.id && (
                                                    <span className="material-symbols-outlined text-blue-500 shrink-0">
                                                        check_circle
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {templates.length === 0 && (
                        <div className="text-center py-6 text-[var(--text-muted)]">
                            <p className="mb-2">No templates available yet.</p>
                            <p className="text-sm">
                                Create templates in Settings → Project Templates
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex gap-2 justify-end p-4 border-t border-[var(--surface-border)]">
                    <button
                        onClick={onClose}
                        disabled={isCreating}
                        className="px-4 py-2 bg-[var(--surface-bg)] text-[var(--text-primary)] rounded-md hover:bg-[var(--button-hover-bg)] transition-colors border border-[var(--input-border)] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={selectedTemplateId === null ? handleCreateBlank : handleConfirm}
                        disabled={isCreating}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isCreating && (
                            <span className="material-symbols-outlined text-[20px] animate-spin">
                                progress_activity
                            </span>
                        )}
                        {isCreating ? 'Creating...' : 'Create Project'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TemplateSelectionModal;

